import Result from 'true-myth/result';
import { z } from 'zod';

import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

const documentSchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    name: z.string(),
    status: z.string(),
    updatedAt: z.string(),
});

const documentsResponseSchema = z.array(documentSchema);

export type DocumentResponse = z.infer<typeof documentSchema>;
export type DocumentsResponse = z.infer<typeof documentsResponseSchema>;

const downloadUrlResponseSchema = z.object({ url: z.string() });

export type DownloadUrlResponse = z.infer<typeof downloadUrlResponseSchema>;

const uploadUrlsResponseSchema = z.record(z.string(), z.string());

export type UploadUrlsResponse = z.infer<typeof uploadUrlsResponseSchema>;

export type DocumentsFetchers = {
    readonly deleteDocument: (documentId: string) => Promise<Result<unknown, HttpFetcherError>>;
    readonly deleteDocuments: (documentIds: readonly string[]) => Promise<Result<void, HttpFetcherError>>;
    readonly downloadDocumentsAsZip: (documentIds: readonly string[]) => Promise<Result<void, HttpFetcherError>>;
    readonly fetchDocuments: (searchTerm?: string) => Promise<Result<DocumentsResponse, HttpFetcherError>>;
    readonly fetchDownloadUrl: (documentId: string) => Promise<Result<DownloadUrlResponse, HttpFetcherError>>;
    readonly getUploadUrls: (fileNames: readonly string[]) => Promise<Result<UploadUrlsResponse, HttpFetcherError>>;
};

export type DocumentsFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createDocumentsFetchers = (dependencies: DocumentsFetchersDependencies): DocumentsFetchers => {
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = dependencies;

    const fetchDocuments: DocumentsFetchers['fetchDocuments'] = async (searchTerm) => {
        return validatingCobrainerApiFetcher.get(documentsResponseSchema, {
            pathname: '/v1/documents',
            requestOptions: {
                searchParams: searchTerm !== undefined ? { searchTerm } : undefined,
            },
        });
    };

    const deleteDocument: DocumentsFetchers['deleteDocument'] = async (documentId) => {
        return cobrainerApiFetcher.delete({
            pathname: `/v1/documents/${documentId}`,
        });
    };

    const deleteDocuments: DocumentsFetchers['deleteDocuments'] = async (documentIds) => {
        const result = await cobrainerApiFetcher.post({
            pathname: '/v1/documents/batch-delete',
            requestOptions: {
                body: JSON.stringify({ documentIds }),
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        });

        if (result.isErr) {
            return Result.err(result.error);
        }

        return Result.ok(undefined);
    };

    const fetchDownloadUrl: DocumentsFetchers['fetchDownloadUrl'] = async (documentId) => {
        return validatingCobrainerApiFetcher.get(downloadUrlResponseSchema, {
            pathname: `/v1/documents/${documentId}/download`,
        });
    };

    const getUploadUrls: DocumentsFetchers['getUploadUrls'] = async (fileNames) => {
        return validatingCobrainerApiFetcher.post(uploadUrlsResponseSchema, {
            pathname: '/v1/documents/presigned-urls',
            requestOptions: {
                body: JSON.stringify(fileNames),
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        });
    };

    const downloadDocumentsAsZip: DocumentsFetchers['downloadDocumentsAsZip'] = async (documentIds) => {
        const result = await cobrainerApiFetcher.post({
            pathname: '/v1/documents/batch-download',
            requestOptions: {
                body: JSON.stringify({ documentIds }),
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        });

        if (result.isErr) {
            return Result.err(result.error);
        }

        return Result.ok(undefined);
    };

    return {
        deleteDocument,
        deleteDocuments,
        downloadDocumentsAsZip,
        fetchDocuments,
        fetchDownloadUrl,
        getUploadUrls,
    };
};
